import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Form, { FormMessage } from '/@/components/form';
import LocalePreferences from '/@/components/locale-preferences';
import SocialButton from '/@/components/social-button';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { withRouter } from '/@/hooks/with-router';
import { _ } from '/@/locale';
import { login, setPreferences, socialLogin } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { getOS, getOSVersion, validateEmail } from '/@/utils';
import { decodeNextUrl, encodeNextUrl } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import Title from '/@/v3/components/title';
import TermsAndConditions from '../../terms-and-conditions';
import styles from './styles.module.css';

/** Same condition as App `shouldHoldFieldviewModalAfterLogin`: stay on login until the FieldView modal is resolved. */
function shouldHoldFieldviewModalAfterLogin(appInstance, user) {
    return Boolean(user && appInstance?.enableFieldviewBlockingModal && !user.validated_type);
}

export const openPrivacyPolicy = () => {
    if (settings.app.privacyPolicy) {
        window.open(settings.app.privacyPolicy, '_blank');
    } else {
        enqueueSnackbar(_('Ha ocurrido un error al cargar la política de privacidad'), {
            variant: 'error',
        });
    }
};

@withRouter
@connect(
    (state) => ({
        country: getSessionCountry(state),
        language: getSessionLanguage(state),
        appInstance: getAppInstance(state),
    }),
    (dispatch) => bindActionCreators({ setPreferences, login, socialLogin, trackEvent }, dispatch)
)
@autobind
export class LoginForm extends Form {
    static contextTypes = {
        history: PropTypes.object,
        login: PropTypes.func,
        socialLogin: PropTypes.func,
        trackEvent: PropTypes.func,
        location: PropTypes.object,
        onClick: PropTypes.func,
    };

    fields = {
        email: { initial: '' },
        password: { initial: '' },
    };

    validation = {
        email: (values) => validateEmail(values.email),
    };

    handleKeyDown(event) {
        event.stopPropagation();
        if (event.keyCode === 13) this.handleLogin();
    }

    handleSocialLogin(provider) {
        return async () => {
            try {
                console.log('handleSocialLogin: PRE');
                await this.props.socialLogin({
                    provider,
                    next: encodeNextUrl(this.props.location),
                    history: this.props.history,
                    source: 'login',
                    onSuccess: () => this.setState({ loading: false, success: '' }),
                });
                console.log('handleSocialLogin: POST');
                this.setState({
                    loading: true,
                    error: '',
                    success: _('Iniciando sesión...'),
                });
            } catch (error) {
                console.log('handleSocialLogin: ERROR');
                this.setState({ loading: false, error: error.message, success: '' });
            }
        };
    }

    async handleLogin() {
        this.setState({ loading: true, error: '' });
        try {
            const session = await this.props.login({ ...this.state.values, onSuccess: this.onSuccessLogin });
            if (!shouldHoldFieldviewModalAfterLogin(this.props.appInstance, session?.user)) {
                this.context.history(decodeNextUrl(this.props.location));
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            this.setState({ loading: false, error: error.message, success: '' });
        }
    }

    showErrorMessage() {
        // Hack to overcome a tricky error message
        const message = this.state.error.includes('IDBDatabase')
            ? _('Ocurrió un error de sincronización en los datos, por favor reintente.')
            : this.state.error;

        return (
            <div className={styles.error}>
                <FormMessage style='error'>{message}</FormMessage>
            </div>
        );
    }

    showSuccessMessage() {
        return (
            <div className={styles.success}>
                <FormMessage style='success'>{this.state.success}</FormMessage>
            </div>
        );
    }

    renderSocial() {
        const isIOS = getOS() === 'iOS';
        const osVersion = Number.parseFloat(getOSVersion());

        const { socialLoginProviders } = settings.app;
        const enableGoogle = socialLoginProviders.includes('google') && !isIOS;
        const enableFacebook = socialLoginProviders.includes('facebook') && (!isIOS || (isIOS && osVersion < 11.3));
        const enableTwitter = socialLoginProviders.includes('twitter');
        const enableFieldView = socialLoginProviders.includes('fieldview');

        return (
            <div className={styles.socialContainer}>
                {enableFieldView && (
                    <div className={styles.linkContainer}>
                        <div className={styles.fieldview} onClick={this.handleSocialLogin('fieldview')} />
                    </div>
                )}
                {enableGoogle && (
                    <SocialButton
                        provider='google'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('google')}
                        circular
                    />
                )}
                {enableFacebook && (
                    <SocialButton
                        provider='facebook'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('facebook')}
                        circular
                    />
                )}
                {enableTwitter && (
                    <SocialButton
                        provider='twitter'
                        disabled={this.state.loading}
                        onClick={this.handleSocialLogin('twitter')}
                        circular
                    />
                )}
            </div>
        );
    }

    renderHeader() {
        return (
            <div className={styles.header}>
                <span id={styles.icon} onClick={this.props.goBack} className='material-icons'>
                    arrow_back
                </span>
                <LocalePreferences
                    openUp
                    appearance='row'
                    showLabels={false}
                    country={this.props.country.id}
                    language={this.props.language}
                    onChange={this.props.setPreferences}
                    showCountry
                    handleCountryChange={() => {}}
                    showLanguage={false}
                />
            </div>
        );
    }

    renderFooter() {
        return (
            <div className={styles.footer}>
                <Title color='light' size='md' className={styles.registerText}>
                    {_('¿Quéres acceder al Catálogo?')}
                </Title>
                <Title
                    color='primary'
                    appearance='text'
                    theme={settings.app.theme}
                    className={styles.registerBtn}
                    size='md'
                    bolder
                    onClick={this.props.goToCatalog}
                >
                    {_('Ingresa aquí')}
                </Title>
            </div>
        );
    }

    render() {
        return (
            <div className={this.props.isMobile && styles.root}>
                {this.props.isMobile && this.renderHeader()}
                <div>
                    <div className={classNames(styles.logo, styles[`theme-${settings.app.theme}`])} />
                    <div>
                        <div className={styles.form} onKeyDown={this.handleKeyDown}>
                            <Input
                                fullWidth
                                type='email'
                                appearance='outline'
                                placeholder={_('E-mail')}
                                size='md'
                                extraClass={styles.input}
                                {...this.getFieldProps('email')}
                                color={settings.app.theme}
                            />
                            <Input
                                color={settings.app.theme}
                                fullWidth
                                type='password'
                                appearance='outline'
                                placeholder={_('Contraseña')}
                                size='md'
                                {...this.getFieldProps('password')}
                                extraClass={styles.input}
                                iconPassword
                            />
                            <div className={styles.resetLink} onClick={() => this.props.handleForgotPassword()}>
                                <Title color='primary' bolder theme={settings.app.theme} className={styles.textButton}>
                                    {_('Olvidé mi contraseña')}
                                </Title>
                            </div>
                            <Button
                                fullWidth
                                variant='contained'
                                disabled={this.state.loading || !this.validate()}
                                onClick={this.handleLogin}
                                sx={{ mb: 2 }}
                            >
                                <div className={styles.flexCenter}>
                                    {this.state.loading ? (
                                        <Fragment>
                                            {_('Cargando...')} <Spinner className={styles.spinner} />
                                        </Fragment>
                                    ) : (
                                        _('Ingresar')
                                    )}
                                </div>
                            </Button>
                            {this.state.error && this.showErrorMessage()}
                            {this.state.success && this.showSuccessMessage()}
                        </div>
                        <Grid container justifyContent='center' direction='column'>
                            <Grid item container justifyContent='center' direction='row'>
                                <Grid item>
                                    <Title color='light' size='md' className={styles.registerText}>
                                        {_('¿Aún no tenes cuenta?')}
                                    </Title>
                                </Grid>
                                <Grid item sx={{ ml: 1 }}>
                                    <Title
                                        color='primary'
                                        appearance='text'
                                        theme={settings.app.theme}
                                        className={styles.registerBtn}
                                        size='md'
                                        bolder
                                        onClick={this.props.onClick}
                                    >
                                        {_('Registrate aquí')}
                                    </Title>
                                </Grid>
                            </Grid>
                            <Grid item container justifyContent='center' sx={{ mt: 1 }}>
                                <Stack spacing={1} alignItems='center'>
                                    <Title
                                        color='primary'
                                        appearance='text'
                                        theme={settings.app.theme}
                                        className={styles.registerBtn}
                                        size='md'
                                        bolder
                                        onClick={openPrivacyPolicy}
                                    >
                                        {_('Política de privacidad')}
                                    </Title>

                                    <Title
                                        color='primary'
                                        appearance='text'
                                        theme={settings.app.theme}
                                        className={styles.registerBtn}
                                        size='md'
                                        bolder
                                        onClick={() => this.setState({ showTerms: true })}
                                    >
                                        {_('Términos y condiciones')}
                                    </Title>
                                </Stack>
                            </Grid>
                        </Grid>
                        <div className={styles.social}>
                            <div>{this.renderSocial()}</div>
                        </div>
                    </div>
                </div>
                {this.props.isMobile && this.renderFooter()}

                <TermsAndConditions
                    visible={this.state.showTerms}
                    onClose={() => this.setState({ showTerms: false })}
                    onAccept={() => this.setState({ showTerms: false })}
                />
            </div>
        );
    }
}
